// src/views/UniverseView.js — Watch Universe (dark space, exoplanet drill-down)
import React, { useState, useMemo, useRef, useEffect } from "react";
import { supabase } from "../lib/supabase";

// ─── Planet palette ───────────────────────────────────────────────────────────

const PLANETS = {
  HMT: {
    light:"#FFD890", base:"#D4720A", dark:"#6A2E00", deep:"#2C1200",
    atm:"#F09030",
    bands:["#E8A050","#C86010","#F4CC80","#B85820","#E09040","#D0701A"],
    rings:true,
  },
  Allwyn: {
    light:"#AADEFD", base:"#1E7ED8", dark:"#083068", deep:"#020C28",
    atm:"#60BAFF",
    bands:["#48B8F8","#1268C0","#78D0FF","#0A50A0","#5AB8F8","#2090E8"],
    rings:false,
  },
  Ricoh: {
    light:"#FFAD90", base:"#C03220", dark:"#580E06", deep:"#220402",
    atm:"#E04828",
    bands:["#D87060","#A01C10","#C04030","#780E08","#C86050","#A82E20"],
    rings:false,
  },
};

const DIAL_COLORS = ["black","white","champagne","silver","blue","green","grey","brown","cream"];

// ─── Star field — deep space: white, blue-white, and warm giant stars ────────

const STARS = Array.from({length:260}, (_,i) => ({
  cx: ((i*7919+333)%997)/997,
  cy: ((i*6271+111)%991)/991,
  r:  i%23===0 ? 2.2 : i%11===0 ? 1.5 : i%5===0 ? 1.0 : 0.45,
  o:  0.06 + (i%9)*0.048,
  // color: most white (#fff), some blue-white (#C8D8FF), rare gold giant (#FFD890)
  col: i%19===0 ? "#FFD890" : i%7===0 ? "#C8D8FF" : "#ffffff",
  twinkle: i%13===0, // these get a subtle cross-shaped sparkle
}));

// India map
const INDIA_POLY = [
  [73.9,34.3],[74.4,34.9],[76.8,35.5],[78.0,34.4],[79.4,32.5],[80.2,30.0],
  [81.9,28.3],[83.3,27.5],[86.5,27.8],[88.0,27.5],[88.9,27.3],[92.0,27.0],
  [96.0,27.5],[95.0,25.5],[93.5,24.0],[92.5,23.0],[92.2,22.0],[91.5,22.5],
  [90.0,22.0],[89.8,22.5],[88.5,21.6],[87.5,21.5],[86.5,20.0],[85.0,19.0],
  [83.5,17.7],[82.0,16.5],[80.3,13.1],[79.9,10.8],[77.5,8.2],[76.5,8.5],
  [76.3,9.5],[74.9,11.9],[73.9,15.5],[73.4,16.0],[72.8,18.9],[72.5,20.6],
  [69.0,22.2],[68.2,23.5],[68.1,24.5],[70.0,27.5],[71.0,29.5],
  [72.5,31.0],[74.0,33.5],[73.9,34.3],
];

const CITY_COORDS = {
  "jaipur":[75.79,26.91],"delhi":[77.21,28.61],"new delhi":[77.21,28.61],
  "mumbai":[72.88,19.08],"bombay":[72.88,19.08],"bangalore":[77.59,12.97],
  "bengaluru":[77.59,12.97],"chennai":[80.27,13.08],"madras":[80.27,13.08],
  "kolkata":[88.36,22.57],"calcutta":[88.36,22.57],"hyderabad":[78.49,17.39],
  "pune":[73.86,18.52],"ahmedabad":[72.59,23.03],"surat":[72.83,21.17],
  "lucknow":[80.95,26.85],"chandigarh":[76.79,30.74],"nagpur":[79.09,21.15],
  "indore":[75.86,22.72],"vadodara":[73.19,22.31],"baroda":[73.19,22.31],
  "coimbatore":[76.97,11.0],"kochi":[76.27,9.93],"cochin":[76.27,9.93],
  "bhopal":[77.41,23.26],"patna":[85.14,25.60],"visakhapatnam":[83.30,17.69],
  "vizag":[83.30,17.69],"agra":[78.01,27.17],"varanasi":[82.97,25.32],
  "amritsar":[74.87,31.63],"ludhiana":[75.85,30.90],"kanpur":[80.35,26.45],
  "mysore":[76.64,12.29],"mysuru":[76.64,12.29],"goa":[74.12,15.30],
  "dehradun":[78.03,30.32],"udaipur":[73.71,24.57],"jodhpur":[73.02,26.29],
  "ajmer":[74.64,26.45],"bikaner":[73.31,28.01],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function projectIndia(lng, lat, W, H, pad = 30) {
  return [pad + (lng-68)/(98-68)*(W-pad*2), pad + (1-(lat-8)/(37-8))*(H-pad*2)];
}

function timeAgoShort(iso) {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m/60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h/24)}d ago`;
}

// Deterministic scatter — same positions every render, different for each item
function deterministicPos(seed, lo, hi) {
  const v = Math.abs(Math.sin(seed * 9301 + 49297) * 233280);
  return lo + (v - Math.floor(v)) * (hi - lo);
}

function scatterPlanets(brands, VW, VH) {
  // Place planets with collision avoidance, seeded by brand name
  const placed = [];
  const pad = 120;
  for (let attempt = 0; attempt < 300; attempt++) {
    for (const b of brands) {
      if (placed.find(p => p.brand === b.brand)) continue;
      const seed = b.brand.charCodeAt(0) + attempt * 1000;
      const cx = deterministicPos(seed,     pad, VW - pad);
      const cy = deterministicPos(seed + 1, pad, VH - pad);
      const r  = 52 + Math.sqrt((b.count||1)) * 4;
      const clash = placed.some(p => Math.hypot(p.cx-cx, p.cy-cy) < p.r + r + 60);
      if (!clash) placed.push({ ...b, cx, cy, r });
    }
    if (placed.length === brands.length) break;
  }
  return placed;
}

function buildVariants(entries) {
  const map = new Map();
  for (const e of entries) {
    const desc = (e.description||"").toLowerCase();
    const dial = DIAL_COLORS.find(c => desc.includes(c)) || "unknown";
    const decade = e.decade || "unknown";
    const key = `${dial}|${decade}`;
    if (!map.has(key)) {
      const dl = dial==="unknown" ? "Unknown Dial" : dial[0].toUpperCase()+dial.slice(1)+" Dial";
      map.set(key, { label:`${dl} · ${decade==="unknown"?"Unknown Era":decade}`, count:0, entries:[] });
    }
    const v = map.get(key); v.count++; v.entries.push(e);
  }
  return [...map.values()].sort((a,b)=>b.count-a.count);
}

function buildUniverse(catalogEntries, watchModels) {
  const approved = (catalogEntries||[]).filter(e=>e.status==="approved");
  return ["HMT","Allwyn","Ricoh"].map(brand => {
    const be = approved.filter(e=>e.brand===brand);
    const bm = (watchModels||[]).filter(wm=>wm.brand===brand);
    const ids = new Set();
    const models = bm.map(wm => {
      const entries = be.filter(e=>e.watchModelId===wm.id);
      entries.forEach(e=>ids.add(e.id));
      return { id:wm.id, model:wm.model, decade:wm.decade||"", count:entries.length, entries, variants:buildVariants(entries) };
    });
    const orphans = be.filter(e=>!ids.has(e.id));
    const oMap = new Map();
    for (const e of orphans) {
      const k=(e.model||"Unknown").toLowerCase().trim();
      if(!oMap.has(k)) oMap.set(k,{model:e.model||"Unknown",entries:[]});
      oMap.get(k).entries.push(e);
    }
    for(const[,og]of oMap) models.push({id:`og-${og.model}`,model:og.model,decade:"",count:og.entries.length,entries:og.entries,variants:buildVariants(og.entries)});
    return { brand, color:PLANETS[brand]?.base||"#888", count:be.length, models:models.filter(m=>m.count>0).sort((a,b)=>b.count-a.count) };
  });
}

function buildCityData(users, sellers) {
  const map = new Map();
  const add = (rawCity, type) => {
    if (!rawCity) return;
    const display = rawCity.trim();
    const key = display.toLowerCase().split(",")[0].trim();
    if (!map.has(key)) map.set(key, { display, sellers:0, buyers:0, total:0, coords:CITY_COORDS[key]||null });
    const c = map.get(key); c[type]++; c.total++;
  };
  (sellers||[]).forEach(s=>add(s.city,"sellers"));
  (users||[]).forEach(u=>add(u.addressCity,"buyers"));
  return [...map.values()].filter(c=>c.coords).sort((a,b)=>b.total-a.total);
}

// ─── SVG helpers ──────────────────────────────────────────────────────────────

function hexParts(hex) {
  if (!hex||hex.length<7) return [0,0,0];
  return [parseInt(hex.slice(1,3),16)/255, parseInt(hex.slice(3,5),16)/255, parseInt(hex.slice(5,7),16)/255];
}

// ─── StarField ────────────────────────────────────────────────────────────────

function StarField({ W, H }) {
  return (
    <g>
      {STARS.map((s,i) => {
        const x=s.cx*W, y=s.cy*H;
        return (
          <g key={i}>
            <circle cx={x} cy={y} r={s.r} fill={s.col} opacity={s.o}/>
            {/* cross-sparkle for bright giant stars */}
            {s.twinkle && s.r>=1.5 && <>
              <line x1={x-s.r*2.8} y1={y} x2={x+s.r*2.8} y2={y} stroke={s.col} strokeWidth="0.4" opacity={s.o*0.5}/>
              <line x1={x} y1={y-s.r*2.8} x2={x} y2={y+s.r*2.8} stroke={s.col} strokeWidth="0.4" opacity={s.o*0.5}/>
            </>}
          </g>
        );
      })}
    </g>
  );
}

// ─── Full-page artifact overlay (fixed, covers entire viewport) ───────────────

function FullPageArtifacts() {
  const G="#D4AF37"; const B="#9AABFF"; const W="#E8EEFF";
  const css=`
    .fpwf{transform-box:fill-box;transform-origin:center;}
    @keyframes fpa{0%,100%{transform:translate(0,0) rotate(-7deg)}50%{transform:translate(12px,16px) rotate(7deg)}}
    @keyframes fpb{0%,100%{transform:translate(0,0) rotate(4deg)}55%{transform:translate(-14px,10px) rotate(-11deg)}}
    @keyframes fpc{0%,100%{transform:translate(0,0)}42%{transform:translate(9px,-12px) rotate(18deg)}}
    @keyframes fpd{0%,100%{transform:translate(0,0) rotate(-5deg)}58%{transform:translate(-10px,13px) rotate(9deg)}}
    @keyframes fpe{0%,100%{transform:translate(0,0) rotate(3deg)}48%{transform:translate(11px,8px) rotate(-12deg)}}
    @keyframes fpf{0%,100%{transform:translate(0,0) rotate(-2deg)}52%{transform:translate(-7px,-10px) rotate(14deg)}}
    @keyframes gcwf{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
    @keyframes gccwf{from{transform:rotate(0deg)}to{transform:rotate(-360deg)}}
    @keyframes starglow{0%,100%{opacity:.05}50%{opacity:.18}}
    @keyframes moonrise{from{opacity:0;transform:translate(0px,0px) scale(0.4)}to{opacity:1;transform:translate(0px,0px) scale(1)}}
    @keyframes zoomFlood{0%{opacity:0}60%{opacity:0}100%{opacity:1}}
  `;
  return (
    <div className="universe-artifacts">
      <svg width="100%" height="100%" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" style={{position:"absolute",inset:0}}>
        <defs><style>{css}</style></defs>

        {/* Subtle star-glow dots */}
        {[230,430,640,820,1060,300,580,920,480,760,660].map((x,i)=>(
          <circle key={i} cx={x} cy={[90,150,55,210,105,390,300,440,530,490,660][i]||200}
            r={[1.6,1.1,1.8,1.3,2.0,1.4,1.1,1.7,1.0,2.1,1.5][i]||1.3}
            fill={i%4===0?G:W} style={{animation:`starglow ${4.5+i*0.6}s ease-in-out infinite`,animationDelay:`${-i*0.9}s`}}/>
        ))}

        {/* Floating watches — interior only (x: 370–1070) */}
        {[
          [400,150,G,11,"fpa 12s","-2s"],[800,100,B,9,"fpb 15s","-8s"],
          [580,770,W,12,"fpc 13s","-5s"],[1070,600,G,8,"fpd 16s","-10s"],
          [440,430,B,10,"fpe 14s","-1s"],[930,280,W,7,"fpf 11s","-6s"],
          [680,700,G,9,"fpa 17s","-12s"],[370,520,B,8,"fpb 12s","-3s"],
        ].map(([x,y,col,r,anim,delay],i)=>(
          <g key={i} transform={`translate(${x},${y})`}>
            <g className="fpwf" style={{animation:anim+" ease-in-out infinite",animationDelay:delay}}>
              <MiniWatch col={col} r={r}/>
            </g>
          </g>
        ))}

        {/* Gears — corners, well inset from extreme edge */}
        {[
          [220,830,W,14,"gcwf 34s"],[1220,75,G,11,"gccwf 25s"],
          [1280,830,B,9,"gcwf 42s -11s"],[210,175,G,8,"gccwf 30s -15s"],
        ].map(([x,y,col,r,anim],i)=>(
          <g key={i} transform={`translate(${x},${y})`}>
            <g className="fpwf" style={{animation:anim+" linear infinite"}}><MiniGear col={col} r={r}/></g>
          </g>
        ))}

        {/* ── Shooting watches — all use SMIL animateTransform (reliable in SVG) ── */}
        {/* L→R  at -8° */}
        <g transform="translate(0,310) rotate(-8)">
          <g><ShootWatch col={G}/>
            <animateTransform attributeName="transform" type="translate" from="-220 0" to="1680 0" dur="19s" repeatCount="indefinite" begin="-5s"/>
          </g>
        </g>
        {/* L→R  at -4° */}
        <g transform="translate(0,690) rotate(-4)">
          <g><ShootWatch col={W}/>
            <animateTransform attributeName="transform" type="translate" from="-220 0" to="1680 0" dur="27s" repeatCount="indefinite" begin="-14s"/>
          </g>
        </g>
        {/* R→L  at +174° */}
        <g transform="translate(1440,410) rotate(174)">
          <g><ShootWatch col={B}/>
            <animateTransform attributeName="transform" type="translate" from="-220 0" to="1680 0" dur="23s" repeatCount="indefinite" begin="-9s"/>
          </g>
        </g>
        {/* R→L  at +170° */}
        <g transform="translate(1440,760) rotate(170)">
          <g><ShootWatch col={G}/>
            <animateTransform attributeName="transform" type="translate" from="-220 0" to="1680 0" dur="31s" repeatCount="indefinite" begin="-20s"/>
          </g>
        </g>
        {/* NE diagonal (bottom-left → top-right, rotate -30°) */}
        <g transform="translate(0,810) rotate(-30)">
          <g><ShootWatch col={W}/>
            <animateTransform attributeName="transform" type="translate" from="-300 0" to="2200 0" dur="26s" repeatCount="indefinite" begin="-3s"/>
          </g>
        </g>
        <g transform="translate(0,900) rotate(-22)">
          <g><ShootWatch col={B}/>
            <animateTransform attributeName="transform" type="translate" from="-300 0" to="2200 0" dur="35s" repeatCount="indefinite" begin="-18s"/>
          </g>
        </g>
        {/* NW diagonal (bottom-right → top-left, rotate -148°) */}
        <g transform="translate(1440,800) rotate(-148)">
          <g><ShootWatch col={G}/>
            <animateTransform attributeName="transform" type="translate" from="-300 0" to="2200 0" dur="29s" repeatCount="indefinite" begin="-8s"/>
          </g>
        </g>
        {/* SE diagonal (top-left → bottom-right, rotate +28°) */}
        <g transform="translate(0,80) rotate(28)">
          <g><ShootWatch col={B}/>
            <animateTransform attributeName="transform" type="translate" from="-300 0" to="2200 0" dur="22s" repeatCount="indefinite" begin="-11s"/>
          </g>
        </g>
        {/* SW diagonal (top-right → bottom-left, rotate +152°) */}
        <g transform="translate(1440,100) rotate(152)">
          <g><ShootWatch col={W}/>
            <animateTransform attributeName="transform" type="translate" from="-300 0" to="2200 0" dur="38s" repeatCount="indefinite" begin="-24s"/>
          </g>
        </g>
        {/* UP diagonal — near vertical */}
        <g transform="translate(720,900) rotate(-82)">
          <g><ShootWatch col={G}/>
            <animateTransform attributeName="transform" type="translate" from="-300 0" to="2200 0" dur="32s" repeatCount="indefinite" begin="-13s"/>
          </g>
        </g>
      </svg>
    </div>
  );
}

// ─── Hyperreal Planet ─────────────────────────────────────────────────────────
// Centered at (0,0); outer <g> does the translate to (cx,cy)

function Planet({ cx, cy, r, brand, count, isHov, onClick, onEnter, onLeave, isParent, isZooming, isFaded }) {
  const pal = PLANETS[brand] || { light:"#ccc", base:"#777", dark:"#333", deep:"#111", atm:"#999", bands:[], rings:false };
  const [rH, rG, rB] = hexParts(pal.atm);
  const rr = r + (isHov && !isZooming ? 6 : 0);

  // Unique IDs per brand
  const gid   = `pg-${brand}`;
  const amid  = `pa-${brand}`;
  const cid   = `pc-${brand}`;
  const bdid  = `pbd-${brand}`;
  const cfid  = `pcf-${brand}`;
  const rbid  = `prb-${brand}`;  // ring back clip
  const rfid  = `prf-${brand}`;  // ring front clip

  const ringBands = [
    [rr*1.28, rr*0.22, rr*0.18, pal.dark,  0.10, 0.18],
    [rr*1.56, rr*0.28, rr*0.26, pal.base,  0.18, 0.32],
    [rr*1.82, rr*0.33, rr*0.22, pal.light, 0.13, 0.24],
    [rr*2.05, rr*0.38, rr*0.14, "#E8D080", 0.08, 0.15],
    [rr*2.22, rr*0.42, rr*0.13, pal.base,  0.10, 0.19],
  ];

  return (
    <g transform={`translate(${cx},${cy})`}
       style={{transition:"opacity 0.25s",opacity:isFaded?0:1}}>
      <defs>
        {/* Deep limb-darkening gradient */}
        <radialGradient id={gid} cx="34%" cy="27%" r="76%">
          <stop offset="0%"   stopColor={pal.light}/>
          <stop offset="22%"  stopColor={pal.base}/>
          <stop offset="58%"  stopColor={pal.dark}/>
          <stop offset="84%"  stopColor={pal.deep}/>
          <stop offset="100%" stopColor="#000" stopOpacity="0.75"/>
        </radialGradient>

        {/* Cloud band gradient — horizontal stripes */}
        <linearGradient id={bdid} x1="0" y1="0" x2="0" y2="1">
          {(pal.bands||[]).map((c,i)=>(
            <stop key={i} offset={`${Math.round(i*(100/(pal.bands.length-1)))}%`} stopColor={c} stopOpacity={0.48+i%2*0.14}/>
          ))}
        </linearGradient>

        {/* Turbulence — warps the band layer into organic clouds */}
        <filter id={cfid} x="-8%" y="-8%" width="116%" height="116%">
          <feTurbulence type="fractalNoise" baseFrequency="0.028 0.009" numOctaves="5" seed={brand.charCodeAt(0)}/>
          <feDisplacementMap in="SourceGraphic" in2="turbulence" scale="18" xChannelSelector="R" yChannelSelector="G"/>
        </filter>

        {/* Planet clip */}
        <clipPath id={cid}><circle cx={0} cy={0} r={rr}/></clipPath>

        {/* Ring clips: back = upper half (far side), front = lower half (near side) */}
        <clipPath id={rbid}><rect x={-rr*3} y={-rr*2.2} width={rr*6} height={rr*2.2}/></clipPath>
        <clipPath id={rfid}><rect x={-rr*3} y={0}       width={rr*6} height={rr*2.2}/></clipPath>

        {/* Atmosphere glow */}
        <filter id={amid} x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation={isHov ? 22 : 13}/>
          <feColorMatrix type="matrix" values={`0 0 0 0 ${rH} 0 0 0 0 ${rG} 0 0 0 0 ${rB} 0 0 0 ${isHov?0.70:0.45} 0`}/>
        </filter>
      </defs>

      {/* Zoom & hover group — scale+fade on click */}
      <g
        onClick={onClick} onMouseEnter={onEnter} onMouseLeave={onLeave}
        style={{
          cursor:"pointer",
          transformBox:"fill-box", transformOrigin:"center",
          transition: isZooming ? "transform 0.60s cubic-bezier(.15,0,.05,1)" : "none",
          transform:  isZooming ? "scale(22)" : "scale(1)",
          opacity:    1,
        }}>

        {/* Atmosphere bloom */}
        <circle cx={0} cy={0} r={rr+26} fill={pal.atm} opacity="0" filter={`url(#${amid})`}/>

        {/* Saturn rings — far side (behind planet) */}
        {pal.rings && (
          <g clipPath={`url(#${rbid})`}>
            {ringBands.map(([rx,ry,sw,col,opBack],i)=>(
              <ellipse key={i} cx={0} cy={rr*0.07} rx={rx} ry={ry} fill="none" stroke={col} strokeWidth={sw} opacity={opBack}/>
            ))}
          </g>
        )}

        {/* Planet sphere */}
        <circle cx={0} cy={0} r={rr} fill={`url(#${gid})`}/>

        {/* Cloud band layer with turbulence warp */}
        <rect x={-rr} y={-rr} width={rr*2} height={rr*2}
          fill={`url(#${bdid})`} clipPath={`url(#${cid})`}
          filter={`url(#${cfid})`} opacity="0.55"/>

        {/* Storm oval (HMT — Great Storm, like GRS) */}
        {brand==="HMT" && (
          <ellipse cx={rr*0.22} cy={rr*0.28} rx={rr*0.22} ry={rr*0.12}
            fill={pal.dark} clipPath={`url(#${cid})`} opacity="0.65"/>
        )}

        {/* Ice cap (Allwyn — polar ice) */}
        {brand==="Allwyn" && (
          <ellipse cx={0} cy={-rr*0.80} rx={rr*0.38} ry={rr*0.20}
            fill="#E0F4FF" clipPath={`url(#${cid})`} opacity="0.40"/>
        )}

        {/* Rocky craters (Ricoh) */}
        {brand==="Ricoh" && [[-rr*0.32,-rr*0.28,rr*0.11],[rr*0.18,-rr*0.42,rr*0.07],[rr*0.35,rr*0.22,rr*0.09]].map(([ex,ey,er],i)=>(
          <circle key={i} cx={ex} cy={ey} r={er} fill={pal.deep} clipPath={`url(#${cid})`} opacity="0.55"/>
        ))}

        {/* Night terminator */}
        <path d={`M 0 ${-rr} A ${rr} ${rr} 0 0 0 0 ${rr}`} fill="#000" opacity="0.44" clipPath={`url(#${cid})`}/>

        {/* Atmospheric limb haze */}
        <circle cx={0} cy={0} r={rr} fill="none" stroke={pal.atm} strokeWidth="5" opacity="0.22"/>

        {/* Saturn rings — near side (in front) */}
        {pal.rings && (
          <g clipPath={`url(#${rfid})`}>
            {ringBands.map(([rx,ry,sw,col,,opFront],i)=>(
              <ellipse key={i} cx={0} cy={rr*0.07} rx={rx} ry={ry} fill="none" stroke={col} strokeWidth={sw} opacity={opFront}/>
            ))}
          </g>
        )}

        {/* Specular highlight */}
        <ellipse cx={-rr*0.27} cy={-rr*0.30} rx={rr*0.21} ry={rr*0.14} fill="#fff" opacity={isHov?0.30:0.19}/>
        <ellipse cx={-rr*0.18} cy={-rr*0.22} rx={rr*0.07} ry={rr*0.045} fill="#fff" opacity="0.70"/>

        {/* Label */}
        {!isParent && <>
          <text x={0} y={rr+(isHov?28:24)} textAnchor="middle"
            fontFamily="'Playfair Display',serif" fontSize={isHov?18:15}
            fill={isHov?"#fff":"#C8C0B0"} fontStyle="italic"
            style={{userSelect:"none",pointerEvents:"none"}}>{brand}</text>
          <text x={0} y={rr+(isHov?46:40)} textAnchor="middle"
            fontFamily="'DM Mono',monospace" fontSize={9}
            fill={isHov?pal.atm:"#5A5080"} letterSpacing="0.07em"
            style={{userSelect:"none",pointerEvents:"none"}}>{count} ENTRIES</text>
        </>}
      </g>
    </g>
  );
}

// ─── Moon component ───────────────────────────────────────────────────────────
// Centered at (0,0); outer <g> translates + applies moonrise animation

function Moon({ cx, cy, r, brand, model, count, decade, isHov, onClick, onEnter, onLeave, animDelay, isZooming }) {
  const pal = PLANETS[brand] || { light:"#bbb", base:"#777", dark:"#333", atm:"#999" };
  const safe = (model||"x").replace(/[^a-z0-9]/gi,"_");
  const gid  = `mg-${brand}-${safe}`;
  const cid  = `mc-${brand}-${safe}`;
  const rr   = r + (isHov && !isZooming ? 4 : 0);

  return (
    <g transform={`translate(${cx},${cy})`}
       style={{animation:`moonrise 0.5s ease-out ${animDelay} both`}}>
      <defs>
        <radialGradient id={gid} cx="34%" cy="28%" r="72%">
          <stop offset="0%"   stopColor={pal.light}/>
          <stop offset="45%"  stopColor={pal.base}/>
          <stop offset="100%" stopColor={pal.dark}/>
        </radialGradient>
        <clipPath id={cid}><circle cx={0} cy={0} r={rr}/></clipPath>
      </defs>

      <g onClick={onClick} onMouseEnter={onEnter} onMouseLeave={onLeave}
         style={{
           cursor:"pointer",
           transformBox:"fill-box", transformOrigin:"center",
           transition: isZooming ? "transform 0.44s cubic-bezier(.4,0,.2,1), opacity 0.36s ease-in" : "none",
           transform:  isZooming ? "scale(5)" : "scale(1)",
           opacity:    isZooming ? 0 : 1,
         }}>

        {/* Hover glow */}
        <circle cx={0} cy={0} r={rr+8} fill={pal.atm||pal.base} opacity={isHov?0.14:0.05}/>
        <circle cx={0} cy={0} r={rr+3} fill="none" stroke={pal.base} strokeWidth="0.8" opacity={isHov?0.35:0.12}/>

        {/* Moon body */}
        <circle cx={0} cy={0} r={rr} fill={`url(#${gid})`}/>

        {/* Filled craters */}
        {[[-rr*0.28,-rr*0.20,rr*0.12,0.32],[rr*0.30,-rr*0.12,rr*0.08,0.26],[-rr*0.08,rr*0.32,rr*0.09,0.22],[rr*0.22,rr*0.26,rr*0.06,0.20]].map(([ex,ey,er,op],i)=>(
          <circle key={i} cx={ex} cy={ey} r={er} fill={pal.dark} clipPath={`url(#${cid})`} opacity={op}/>
        ))}

        {/* Night terminator */}
        <path d={`M 0 ${-rr} A ${rr} ${rr} 0 0 0 0 ${rr}`} fill="#000" opacity="0.44" clipPath={`url(#${cid})`}/>

        {/* Specular */}
        <ellipse cx={-rr*0.27} cy={-rr*0.28} rx={rr*0.18} ry={rr*0.12} fill="#fff" opacity={isHov?0.26:0.16}/>
        <ellipse cx={-rr*0.18} cy={-rr*0.20} rx={rr*0.05} ry={rr*0.03} fill="#fff" opacity="0.65"/>

        {/* Label */}
        <text x={0} y={rr+16} textAnchor="middle"
          fontFamily="'Playfair Display',serif" fontSize={isHov?14:12}
          fill={isHov?"#fff":"#B0A898"} fontStyle="italic"
          style={{userSelect:"none",pointerEvents:"none"}}>{model}</text>
        {decade && <text x={0} y={rr+28} textAnchor="middle"
          fontFamily="'DM Mono',monospace" fontSize={7.5} fill={pal.atm} opacity={0.65}
          style={{userSelect:"none",pointerEvents:"none"}}>{decade}</text>}
        <text x={0} y={rr+(decade?40:28)} textAnchor="middle"
          fontFamily="'DM Mono',monospace" fontSize={8}
          fill={isHov?pal.atm:pal.base} opacity={0.80}
          style={{userSelect:"none",pointerEvents:"none"}}>{count}</text>
      </g>
    </g>
  );
}

// ─── Watch artifacts ──────────────────────────────────────────────────────────

function MiniWatch({ col, r = 11 }) {
  return (
    <g opacity={0.14}>
      {/* Elegant strap lugs */}
      <rect x={-r*0.26} y={-(r+5.5)} width={r*0.52} height={5.5} rx="1.2" fill={col} opacity="0.6"/>
      <rect x={-r*0.26} y={r}        width={r*0.52} height={5.5} rx="1.2" fill={col} opacity="0.6"/>
      {/* Case bezel */}
      <circle r={r} fill="none" stroke={col} strokeWidth="1.0"/>
      {/* Inner dial ring */}
      <circle r={r*0.85} fill="none" stroke={col} strokeWidth="0.35" opacity="0.4"/>
      {/* Crown */}
      <rect x={r+0.5} y={-2} width={4.5} height={4} rx="1.5" fill={col} opacity="0.7"/>
      {/* 12 hour markers */}
      {Array.from({length:12},(_,i)=>{
        const a=i*30*Math.PI/180; const isH=i%3===0;
        const r1=r-1.2, r2=r-3.5-(isH?0.8:0);
        return <line key={i} x1={Math.sin(a)*r1} y1={-Math.cos(a)*r1} x2={Math.sin(a)*r2} y2={-Math.cos(a)*r2}
          stroke={col} strokeWidth={isH?"0.9":"0.45"} strokeLinecap="round"/>;
      })}
      {/* Hands — thin and elegant */}
      <line x1="0" y1={r*0.12} x2={-r*0.30} y2={-r*0.42} stroke={col} strokeWidth="1.1" strokeLinecap="round"/>
      <line x1="0" y1={r*0.12} x2="0"       y2={-r*0.60} stroke={col} strokeWidth="0.7" strokeLinecap="round"/>
      <line x1="0" y1={r*0.14} x2="0"       y2={-r*0.58} stroke={col} strokeWidth="0.4" strokeLinecap="round" opacity="0.5"/>
      <circle r="1.2" fill={col}/>
    </g>
  );
}

function MiniGear({ col, r = 12 }) {
  const teeth = 8;
  const pts = Array.from({length:teeth*2},(_,i)=>{
    const ang=(i*Math.PI)/teeth-Math.PI/2;
    const rr=i%2===0?r:r-4;
    return `${Math.cos(ang)*rr},${Math.sin(ang)*rr}`;
  });
  return (
    <g opacity={0.16}>
      <polygon points={pts.join(" ")} fill="none" stroke={col} strokeWidth="1.5"/>
      <circle r={r*0.38} fill="none" stroke={col} strokeWidth="1.5"/>
      <circle r={r*0.12} fill={col}/>
    </g>
  );
}

function ShootWatch({ col }) {
  return (
    <g opacity={0.36}>
      <ellipse cx={-52} cy={0} rx={44} ry={3.5} fill={col} opacity={0.10}/>
      <ellipse cx={-30} cy={0} rx={22} ry={2}   fill={col} opacity={0.18}/>
      <rect x={-7} y={-17} width={14} height={5} rx="2" fill={col}/>
      <rect x={-7} y={12}  width={14} height={5} rx="2" fill={col}/>
      <circle r={13} fill="none" stroke={col} strokeWidth="1.8"/>
      <rect x={-19} y={-3.5} width={7} height={7} rx="2.5" fill={col}/>
      <circle r={7.5} fill="none" stroke={col} strokeWidth="0.8"/>
      <line x1="0" y1="0" x2={0}   y2={-5.5} stroke={col} strokeWidth="1.8" strokeLinecap="round"/>
      <line x1="0" y1="0" x2={4.5} y2={3}    stroke={col} strokeWidth="1.2" strokeLinecap="round"/>
      <circle r="2" fill={col}/>
    </g>
  );
}

function AnimatedBackground({ VW, VH }) {
  const G="#D97706"; const E="#059669"; const B="#2563EB";
  const css=`
    .wf{transform-box:fill-box;transform-origin:center;}
    @keyframes da{0%,100%{transform:translate(0,0) rotate(-8deg)}50%{transform:translate(13px,17px) rotate(8deg)}}
    @keyframes db{0%,100%{transform:translate(0,0) rotate(5deg)}60%{transform:translate(-15px,11px) rotate(-12deg)}}
    @keyframes dc{0%,100%{transform:translate(0,0)}40%{transform:translate(10px,-13px) rotate(18deg)}}
    @keyframes dd{0%,100%{transform:translate(0,0) rotate(-4deg)}55%{transform:translate(-9px,14px) rotate(9deg)}}
    @keyframes gcw{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
    @keyframes gccw{from{transform:rotate(0deg)}to{transform:rotate(-360deg)}}
    @keyframes moonrise{from{opacity:0;transform:scale(0.4)}to{opacity:1;transform:scale(1)}}
  `;
  return (
    <g>
      <defs><style>{css}</style></defs>
      <g transform={`translate(${VW*0.06},${VH*0.18})`}>
        <g className="wf" style={{animation:"da 9s ease-in-out infinite"}}><MiniWatch col={G} r={11}/></g>
      </g>
      <g transform={`translate(${VW*0.84},${VH*0.28})`}>
        <g className="wf" style={{animation:"db 12s ease-in-out infinite"}}><MiniWatch col={B} r={8}/></g>
      </g>
      <g transform={`translate(${VW*0.47},${VH*0.08})`}>
        <g className="wf" style={{animation:"dc 10s ease-in-out infinite"}}><MiniWatch col={E} r={12}/></g>
      </g>
      <g transform={`translate(${VW*0.73},${VH*0.82})`}>
        <g className="wf" style={{animation:"dd 13s ease-in-out infinite"}}><MiniWatch col={G} r={8}/></g>
      </g>
      <g transform={`translate(${VW*0.11},${VH*0.74})`}>
        <g className="wf" style={{animation:"gcw 28s linear infinite"}}><MiniGear col={E} r={13}/></g>
      </g>
      <g transform={`translate(${VW*0.90},${VH*0.10})`}>
        <g className="wf" style={{animation:"gccw 20s linear infinite"}}><MiniGear col={B} r={9}/></g>
      </g>
      <g transform={`translate(0,${VH*0.30}) rotate(-12)`}>
        <g><ShootWatch col={G}/>
          <animateTransform attributeName="transform" type="translate" from={`-170 0`} to={`${VW+170} 0`} dur="18s" repeatCount="indefinite" begin="-3s"/>
        </g>
      </g>
      <g transform={`translate(${VW},${VH*0.65}) rotate(168)`}>
        <g><ShootWatch col={E}/>
          <animateTransform attributeName="transform" type="translate" from={`0 0`} to={`${-(VW+340)} 0`} dur="24s" repeatCount="indefinite" begin="-12s"/>
        </g>
      </g>
    </g>
  );
}

// ─── Solar System (main drill-down) ───────────────────────────────────────────

function SolarSystem({ catalogEntries, watchModels, setView }) {
  const [level,    setLevel]  = useState("planets");
  const [selBrand, setSel]    = useState(null);
  const [selModel, setMoon]   = useState(null);
  const [hovered,  setHov]    = useState(null);
  const [selEntry, setEntry]  = useState(null);   // catalog entry detail overlay
  const [detailIdx,setDetIdx] = useState(0);       // photo gallery index
  const wrapRef = useRef(null);
  const [wrapW, setWrapW]     = useState(720);

  useEffect(()=>{
    if (!wrapRef.current) return;
    setWrapW(wrapRef.current.offsetWidth||720);
    if (!window.ResizeObserver) return;
    const ro = new ResizeObserver(es=>setWrapW(es[0].contentRect.width));
    ro.observe(wrapRef.current);
    return ()=>ro.disconnect();
  },[]);

  const universe  = useMemo(()=>buildUniverse(catalogEntries, watchModels),[catalogEntries,watchModels]);
  const activeBrands = universe.filter(b=>b.count>0);
  const brandData = universe.find(b=>b.brand===selBrand);

  const isMobile = wrapW < 480;
  const VW = Math.max(wrapW, 300);
  const VH = Math.min(Math.max(VW * 0.60, 280), 540);

  // Scatter planets deterministically
  const planets = useMemo(()=>scatterPlanets(activeBrands, VW, VH),[activeBrands, VW, VH]);

  // Orbit moons around center of canvas
  const centerX = VW / 2;
  const centerY = VH / 2;
  const orbitRx  = Math.min(VW, VH) * 0.30;
  const orbitRy  = orbitRx * 0.55;

  const moons = useMemo(()=>{
    if (!brandData) return [];
    const ms = brandData.models;
    const maxC = Math.max(...ms.map(m=>m.count||1));
    return ms.map((m,i)=>{
      const angle = (i / ms.length) * Math.PI * 2 - Math.PI/2;
      return {
        ...m,
        cx: centerX + orbitRx * Math.cos(angle),
        cy: centerY + orbitRy * Math.sin(angle),
        r: 14 + 20 * Math.sqrt((m.count||1)/maxC),
      };
    });
  },[brandData, centerX, centerY, orbitRx, orbitRy]);

  const parentPlanet = planets.find(p=>p.brand===selBrand);
  const pal = PLANETS[selBrand] || {};

  // Zoom-in transitions
  const [zoomBrand, setZoomBrand] = useState(null);
  const [zoomMoon,  setZoomMoon]  = useState(null);

  const handlePlanetClick = brand => {
    if (zoomBrand) return;
    setZoomBrand(brand);
    setHov(null);
    setTimeout(()=>{ setZoomBrand(null); setSel(brand); setLevel("moons"); }, 540);
  };
  const handleMoonClick = moon => {
    if (zoomMoon) return;
    setZoomMoon(moon.id);
    setHov(null);
    setTimeout(()=>{ setZoomMoon(null); setMoon(moon); setLevel("variants"); }, 460);
  };
  const goBack = () => {
    if (level==="variants") { setLevel("moons"); setMoon(null); }
    else { setLevel("planets"); setSel(null); }
    setHov(null);
  };

  const crumbs = [
    { label:"Universe", fn:()=>{ setLevel("planets"); setSel(null); setMoon(null); } },
    ...(selBrand ? [{ label:selBrand, fn:level==="variants"?()=>{ setLevel("moons"); setMoon(null); }:null }] : []),
    ...(selModel ? [{ label:selModel.model, fn:null }] : []),
  ];

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:12,flexWrap:"wrap"}}>
        {crumbs.map((c,i)=>(
          <React.Fragment key={i}>
            {i>0&&<span style={{color:"#3A3A50",fontSize:12}}>›</span>}
            <button onClick={c.fn||undefined} disabled={!c.fn}
              style={{background:"none",border:"none",fontFamily:"'DM Mono',monospace",fontSize:10,letterSpacing:"0.1em",textTransform:"uppercase",color:c.fn?pal.atm||"#6EE7B7":"#3A3A50",cursor:c.fn?"pointer":"default",padding:0}}>
              {c.label}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Title */}
      <div style={{display:"flex",alignItems:"baseline",gap:12,marginBottom:level==="planets"?8:4}}>
        <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:28,margin:0,color:"#EDE0CC",letterSpacing:"-0.02em",fontStyle:"italic"}}>
          {level==="planets"?"Watch Universe":level==="moons"?selBrand:selModel?.model}
        </h2>
        <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#3A3050",letterSpacing:"0.1em"}}>
          {level==="planets"
            ? `${activeBrands.length} BRANDS`
            : level==="moons"
              ? `${moons.length} MODELS · ${brandData?.count||0} ENTRIES`
              : `${selModel?.variants?.reduce((s,v)=>s+v.entries.length,0)||0} WATCHES`}
        </span>
      </div>
      {level==="planets" && (
        <p style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#2A2440",letterSpacing:"0.08em",marginBottom:16,marginTop:0}}>
          TAP A BRAND TO EXPLORE →
        </p>
      )}
      {level==="moons" && (
        <p style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#2A2440",letterSpacing:"0.08em",marginBottom:16,marginTop:0}}>
          TAP A MODEL TO SEE WATCHES →
        </p>
      )}

      <div ref={wrapRef} style={{width:"100%"}}>
        {/* ── Planet view ── */}
        {level==="planets" && (
          isMobile ? (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {activeBrands.map(b=>{
                const p=PLANETS[b.brand]||{};
                return (
                  <div key={b.brand} onClick={()=>handlePlanetClick(b.brand)}
                    style={{background:"#0C0A1A",border:`1.5px solid ${p.base}30`,borderRadius:14,padding:"16px 18px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",transition:"border-color .15s"}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=p.base+"70"}
                    onMouseLeave={e=>e.currentTarget.style.borderColor=p.base+"30"}>
                    <div>
                      <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,color:"#EDE0CC",fontStyle:"italic"}}>{b.brand}</div>
                      <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:p.atm,marginTop:3}}>{b.models.length} models</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontFamily:"'Playfair Display',serif",fontSize:26,color:p.base,lineHeight:1}}>{b.count}</div>
                      <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"#3A2E20",marginTop:2}}>entries</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <svg width={VW} height={VH} style={{display:"block",borderRadius:16,overflow:"hidden"}}>
              <defs>
                <radialGradient id="spc1" cx="50%" cy="40%" r="75%">
                  <stop offset="0%"  stopColor="#080618"/>
                  <stop offset="100%" stopColor="#000008"/>
                </radialGradient>
                {/* Nebula wisps */}
                <radialGradient id="neb1" cx="22%" cy="38%" r="38%">
                  <stop offset="0%" stopColor="#3010A0" stopOpacity="0.10"/>
                  <stop offset="100%" stopColor="#000" stopOpacity="0"/>
                </radialGradient>
                <radialGradient id="neb2" cx="76%" cy="60%" r="42%">
                  <stop offset="0%" stopColor="#003880" stopOpacity="0.12"/>
                  <stop offset="100%" stopColor="#000" stopOpacity="0"/>
                </radialGradient>
                <radialGradient id="neb3" cx="55%" cy="18%" r="28%">
                  <stop offset="0%" stopColor="#1C2860" stopOpacity="0.08"/>
                  <stop offset="100%" stopColor="#000" stopOpacity="0"/>
                </radialGradient>
              </defs>
              <rect width={VW} height={VH} fill="url(#spc1)"/>
              <rect width={VW} height={VH} fill="url(#neb1)"/>
              <rect width={VW} height={VH} fill="url(#neb2)"/>
              <rect width={VW} height={VH} fill="url(#neb3)"/>
              <StarField W={VW} H={VH}/>
              {planets.map(p=>(
                <Planet key={p.brand} cx={p.cx} cy={p.cy} r={p.r} brand={p.brand}
                  count={p.count} isHov={hovered===p.brand}
                  isZooming={zoomBrand===p.brand}
                  isFaded={!!zoomBrand && zoomBrand!==p.brand}
                  onClick={()=>handlePlanetClick(p.brand)}
                  onEnter={()=>!zoomBrand&&setHov(p.brand)}
                  onLeave={()=>setHov(null)}/>
              ))}
              {/* Color flood: fades in as planet expands to fill view */}
              {zoomBrand&&(
                <rect width={VW} height={VH} fill={PLANETS[zoomBrand]?.deep||"#000"}
                  style={{animation:"zoomFlood 0.6s ease-in forwards",pointerEvents:"none"}}/>
              )}
            </svg>
          )
        )}

        {/* ── Moon view ── */}
        {level==="moons" && !isMobile && (
          <svg width={VW} height={VH} style={{display:"block",borderRadius:16,overflow:"hidden"}}>
            <defs>
              <radialGradient id="spc2" cx="50%" cy="40%" r="75%">
                <stop offset="0%" stopColor="#080618"/><stop offset="100%" stopColor="#000008"/>
              </radialGradient>
              <radialGradient id="neb4" cx={`${centerX/VW*100}%`} cy={`${centerY/VH*100}%`} r="55%">
                <stop offset="0%" stopColor={pal.atm||"#4040A0"} stopOpacity="0.08"/>
                <stop offset="100%" stopColor="#000" stopOpacity="0"/>
              </radialGradient>
            </defs>
            <rect width={VW} height={VH} fill="url(#spc2)"/>
            <rect width={VW} height={VH} fill="url(#neb4)"/>
            <StarField W={VW} H={VH}/>
            {/* Orbit path */}
            <ellipse cx={centerX} cy={centerY} rx={orbitRx} ry={orbitRy}
              fill="none" stroke={pal.base||"#888"} strokeWidth="0.6"
              strokeDasharray="5 9" opacity="0.18"/>

            {/* Parent planet — shrunk, centered */}
            <Planet cx={centerX} cy={centerY} r={40} brand={selBrand}
              count={brandData?.count||0} isHov={false} isZooming={false}
              isParent onClick={()=>{}} onEnter={()=>{}} onLeave={()=>{}}/>

            {/* Moons */}
            {moons.map((m,i)=>(
              <Moon key={m.id} cx={m.cx} cy={m.cy} r={m.r}
                brand={selBrand} model={m.model} count={m.count} decade={m.decade}
                isHov={hovered===m.id} isZooming={zoomMoon===m.id}
                animDelay={`${i*0.08}s`}
                onClick={()=>handleMoonClick(m)}
                onEnter={()=>!zoomMoon&&setHov(m.id)}
                onLeave={()=>setHov(null)}/>
            ))}
          </svg>
        )}

        {/* Moon view — mobile */}
        {level==="moons" && isMobile && (
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {moons.map(m=>{
              const p=PLANETS[selBrand]||{};
              return (
                <div key={m.id} onClick={()=>handleMoonClick(m)}
                  style={{background:"#0C0A1A",border:`1.5px solid ${p.base}30`,borderRadius:12,padding:"14px 16px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center"}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=p.base+"60"}
                  onMouseLeave={e=>e.currentTarget.style.borderColor=p.base+"30"}>
                  <div>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,color:"#EDE0CC",fontStyle:"italic"}}>{m.model}</div>
                    {m.decade&&<div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"#3A3050",marginTop:3}}>{m.decade}</div>}
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,color:p.base,lineHeight:1}}>{m.count}</div>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"#3A3050"}}>entries</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Variant view — catalog entries, clickable ── */}
        {level==="variants" && (
          <>
          {selEntry && (
            // ── Entry detail overlay ──
            <>
              <div style={{position:"fixed",inset:0,zIndex:200,background:"rgba(0,0,10,.82)",backdropFilter:"blur(8px)"}} onClick={()=>setEntry(null)}/>
              <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",zIndex:201,background:"#06060F",border:`1px solid ${PLANETS[selBrand]?.base||"#333"}40`,borderRadius:18,width:"min(92vw,560px)",maxHeight:"85vh",overflowY:"auto",boxShadow:"0 24px 80px rgba(0,0,0,.7)"}}>
                {/* Photo gallery */}
                {selEntry.photos?.length>0 && (
                  <div style={{position:"relative",height:280,background:"#020208",borderRadius:"18px 18px 0 0",overflow:"hidden"}}>
                    <img src={selEntry.photos[detailIdx]} alt={selEntry.model}
                      style={{width:"100%",height:"100%",objectFit:"contain",objectPosition:"center"}}/>
                    {selEntry.photos.length>1 && (
                      <div style={{position:"absolute",bottom:10,left:0,right:0,display:"flex",justifyContent:"center",gap:6}}>
                        {selEntry.photos.map((_,i)=>(
                          <div key={i} onClick={ev=>{ev.stopPropagation();setDetIdx(i);}}
                            style={{width:6,height:6,borderRadius:"50%",background:i===detailIdx?(PLANETS[selBrand]?.atm||"#fff"):"rgba(255,255,255,.3)",cursor:"pointer",transition:"background .15s"}}/>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div style={{padding:"20px 24px 24px"}}>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:PLANETS[selBrand]?.atm||"#888",letterSpacing:"0.14em",marginBottom:6}}>{selBrand}</div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:24,color:"#EDE0CC",fontStyle:"italic",marginBottom:10,lineHeight:1.2}}>{selEntry.model}</div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
                    {selEntry.decade&&<span style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:PLANETS[selBrand]?.atm,background:PLANETS[selBrand]?.base+"20",borderRadius:4,padding:"3px 8px"}}>{selEntry.decade}</span>}
                    {selEntry.condition&&<span style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"#6EE7B7",background:"#0A2014",borderRadius:4,padding:"3px 8px"}}>{selEntry.condition}</span>}
                    {selEntry.movement&&<span style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"#9AABFF",background:"#0E0E28",borderRadius:4,padding:"3px 8px"}}>⚙ {selEntry.movement}</span>}
                  </div>
                  {selEntry.description&&<p style={{fontFamily:"'DM Sans',sans-serif",fontSize:13,color:"#7A708A",lineHeight:1.7,marginBottom:18}}>{selEntry.description}</p>}
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={()=>setEntry(null)}
                      style={{flex:1,background:"none",border:"1px solid #2A2440",borderRadius:10,padding:"10px",fontFamily:"'DM Mono',monospace",fontSize:10,color:"#4A4060",cursor:"pointer",letterSpacing:"0.06em"}}>
                      ← Back
                    </button>
                    {setView && <button onClick={()=>{setEntry(null);if(setView)setView("catalog");}}
                      style={{flex:2,background:PLANETS[selBrand]?.base||"#333",border:"none",borderRadius:10,padding:"10px",fontFamily:"'DM Mono',monospace",fontSize:10,color:"#fff",cursor:"pointer",fontWeight:600,letterSpacing:"0.06em"}}>
                      View in Catalog →
                    </button>}
                  </div>
                </div>
              </div>
            </>
          )}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:14}}>
            {(selModel?.variants||[]).flatMap(v=>v.entries).map((e,i)=>{
              const p=PLANETS[selBrand]||{};
              const photo=e.photos?.[0];
              return (
                <div key={e.id||i}
                  onClick={()=>{setEntry(e);setDetIdx(0);}}
                  style={{background:"#0A0614",border:`1px solid ${p.base}25`,borderRadius:14,overflow:"hidden",transition:"border-color .2s,transform .2s",cursor:"pointer"}}
                  onMouseEnter={el=>{el.currentTarget.style.borderColor=p.base+"70";el.currentTarget.style.transform="translateY(-4px)";}}
                  onMouseLeave={el=>{el.currentTarget.style.borderColor=p.base+"25";el.currentTarget.style.transform="translateY(0)";}}>
                  <div style={{height:148,background:"#060410",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {photo
                      ? <img src={photo} alt={e.model} style={{width:"100%",height:"100%",objectFit:"contain"}}/>
                      : <svg width={36} height={36} viewBox="0 0 40 40" fill="none">
                          <circle cx={20} cy={20} r={16} stroke={p.base} strokeWidth="1.5" opacity="0.4"/>
                          <circle cx={20} cy={20} r={10} stroke={p.atm||p.base} strokeWidth="1" opacity="0.3"/>
                          <circle cx={20} cy={20} r={3} fill={p.base} opacity="0.5"/>
                        </svg>}
                  </div>
                  <div style={{padding:"11px 13px"}}>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:14,color:"#EDE0CC",fontStyle:"italic",marginBottom:4,lineHeight:1.25}}>{e.brand} {e.model}</div>
                    <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:5}}>
                      {e.decade&&<span style={{fontFamily:"'DM Mono',monospace",fontSize:7.5,color:p.atm||"#D97706",background:p.base+"18",borderRadius:3,padding:"2px 5px"}}>{e.decade}</span>}
                      {e.condition&&<span style={{fontFamily:"'DM Mono',monospace",fontSize:7.5,color:"#6EE7B7",background:"#0A2014",borderRadius:3,padding:"2px 5px"}}>{e.condition}</span>}
                    </div>
                    {e.description&&<div style={{fontFamily:"'DM Sans',sans-serif",fontSize:10.5,color:"#3A3050",lineHeight:1.4,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{e.description}</div>}
                    <div style={{marginTop:8,fontFamily:"'DM Mono',monospace",fontSize:8,color:p.base,opacity:.6}}>TAP TO VIEW →</div>
                  </div>
                </div>
              );
            })}
            {(!selModel?.variants?.length || selModel.variants.flatMap(v=>v.entries).length===0) && (
              <div style={{gridColumn:"1/-1",textAlign:"center",padding:"60px 0",fontFamily:"'DM Mono',monospace",fontSize:11,color:"#2A2440",letterSpacing:"0.1em"}}>
                NO ENTRIES IN THIS ORBIT
              </div>
            )}
          </div>
          </>
        )}
      </div>

      {level!=="planets" && (
        <button onClick={goBack}
          style={{background:"none",border:`1px solid ${pal.base||"#2A2040"}40`,borderRadius:20,padding:"8px 18px",color:"#4A405A",fontFamily:"'DM Mono',monospace",fontSize:10,cursor:"pointer",letterSpacing:"0.08em",marginTop:20,display:"inline-flex",alignItems:"center",gap:6,transition:"border-color .15s,color .15s"}}
          onMouseEnter={e=>{e.currentTarget.style.borderColor=(pal.atm||"#059669")+"80";e.currentTarget.style.color=pal.atm||"#059669";}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor=(pal.base||"#2A2040")+"40";e.currentTarget.style.color="#4A405A";}}>
          ← {level==="moons"?"All Planets":`${selBrand} Moons`}
        </button>
      )}
    </div>
  );
}

// ─── Community Map ────────────────────────────────────────────────────────────

function CommunityMap({ users, sellers, visits }) {
  const [hov, setHov]      = useState(null);
  const wrapRef = useRef(null);
  const [wrapW, setWrapW]  = useState(640);

  useEffect(()=>{
    if (!wrapRef.current) return;
    setWrapW(wrapRef.current.offsetWidth||640);
    if (!window.ResizeObserver) return;
    const ro = new ResizeObserver(es=>setWrapW(es[0].contentRect.width));
    ro.observe(wrapRef.current);
    return ()=>ro.disconnect();
  },[]);

  const cities    = useMemo(()=>buildCityData(users, sellers),[users,sellers]);
  const totalPins = cities.reduce((s,c)=>s+c.total,0);
  const VW        = Math.max(wrapW,280);
  const VH        = Math.round(VW*0.88);
  const maxCount  = cities.length ? Math.max(...cities.map(c=>c.total)) : 1;
  const indiaPoints = INDIA_POLY.map(([lng,lat])=>projectIndia(lng,lat,VW,VH).join(",")).join(" ");

  const visitsByCity = useMemo(()=>{
    const map = new Map();
    (visits||[]).forEach(v=>{
      const key=(v.city||"").toLowerCase().split(",")[0].trim();
      if (!key||!CITY_COORDS[key]) return;
      if (!map.has(key)) map.set(key,{city:v.city,coords:CITY_COORDS[key],visitors:[]});
      map.get(key).visitors.push(v);
    });
    return [...map.values()];
  },[visits]);

  const recentVisitors = useMemo(()=>{
    const seen = new Map();
    (visits||[]).forEach(v=>{ if (!seen.has(v.userId)||new Date(v.visitedAt)>new Date(seen.get(v.userId).visitedAt)) seen.set(v.userId,v); });
    return [...seen.values()].sort((a,b)=>new Date(b.visitedAt)-new Date(a.visitedAt));
  },[visits]);

  const roleColor = r=>r==="seller"?"#D97706":r==="admin"?"#E11D48":r==="collector"?"#D97706":"#059669";

  return (
    <div>
      <div style={{marginBottom:14}}>
        <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:30,margin:0,color:"#EDE0CC",letterSpacing:"-0.02em",fontStyle:"italic"}}>Community Map</h2>
        <p style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#3A3050",letterSpacing:"0.1em",marginTop:4,marginBottom:0}}>
          {totalPins} MEMBERS · {cities.length} CITIES · {recentVisitors.length} UNIVERSE VISITORS
        </p>
      </div>

      <div ref={wrapRef} style={{width:"100%",background:"#080614",borderRadius:16,border:"1px solid #1A1530",overflow:"hidden"}}>
        <svg width={VW} height={VH} style={{display:"block"}}>
          <defs>
            <radialGradient id="mapbg" cx="50%" cy="50%" r="70%">
              <stop offset="0%" stopColor="#10142A"/><stop offset="100%" stopColor="#060410"/>
            </radialGradient>
            <filter id="cglow" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="5" result="blur"/>
              <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
          </defs>
          <rect width={VW} height={VH} fill="url(#mapbg)"/>
          <StarField W={VW} H={VH}/>
          <polygon points={indiaPoints} fill="#0A0820" opacity="0.8" transform="translate(2,3)"/>
          <polygon points={indiaPoints} fill="#141038" stroke="#2A2060" strokeWidth="1.5"/>

          {cities.map(city=>{
            const [cx,cy]=projectIndia(city.coords[0],city.coords[1],VW,VH);
            const isHov=hov===city.display;
            const r=5+(city.total/maxCount)*14;
            const col=city.sellers>0?"#D97706":"#059669";
            return (
              <g key={city.display} onMouseEnter={()=>setHov(city.display)} onMouseLeave={()=>setHov(null)} style={{cursor:"default"}}>
                <circle cx={cx} cy={cy} r={r+7} fill="none" stroke={col} strokeWidth={1} opacity={isHov?0.3:0.1}/>
                <circle cx={cx} cy={cy} r={r} fill={col} opacity={isHov?1:0.82} filter={isHov?"url(#cglow)":undefined}/>
                <text x={cx} y={cy+r+13} textAnchor="middle" fontFamily="'DM Mono',monospace" fontSize={isHov?10:8.5}
                  fill={col} opacity={isHov?1:0.60} fontWeight={isHov?"700":"400"} style={{userSelect:"none"}}>
                  {city.display.toUpperCase()}
                </text>
              </g>
            );
          })}

          {visitsByCity.map(vc=>{
            const [cx,cy]=projectIndia(vc.coords[0],vc.coords[1],VW,VH);
            const n=vc.visitors.length;
            return (
              <g key={vc.city} onMouseEnter={()=>setHov("uv-"+vc.city)} onMouseLeave={()=>setHov(null)} style={{cursor:"default"}}>
                <polygon points={`${cx},${cy-9} ${cx+7},${cy} ${cx},${cy+9} ${cx-7},${cy}`}
                  fill="#059669" stroke="#1A3A2A" strokeWidth="1.2" opacity={hov==="uv-"+vc.city?1:0.75}
                  filter={hov==="uv-"+vc.city?"url(#cglow)":undefined}/>
                <text x={cx} y={cy+4} textAnchor="middle" fontFamily="'DM Mono',monospace" fontSize={7} fill="#fff" fontWeight="700" style={{userSelect:"none"}}>{n}</text>
              </g>
            );
          })}

          {hov&&(()=>{
            const isVis=String(hov).startsWith("uv-");
            if (isVis) {
              const vc=visitsByCity.find(v=>v.city===hov.slice(3));
              if (!vc) return null;
              const [cx,cy]=projectIndia(vc.coords[0],vc.coords[1],VW,VH);
              const tx=cx>VW*0.7?cx-160:cx+14; const ty=cy>VH*0.8?cy-80:cy-10;
              const rows=Math.min(vc.visitors.length,4);
              return (<g>
                <rect x={tx} y={ty} width={148} height={rows*16+28} rx={6} fill="#0E0C22" stroke="#2A2050" strokeWidth={1}/>
                <text x={tx+10} y={ty+16} fontFamily="'DM Mono',monospace" fontSize={9} fill="#059669" fontWeight="700">◆ {vc.visitors.length} VISITORS</text>
                {vc.visitors.slice(0,rows).map((v,i)=>(
                  <text key={i} x={tx+10} y={ty+30+i*16} fontFamily="'DM Mono',monospace" fontSize={8} fill="#8080A0">{v.userName} · {v.role||"user"}</text>
                ))}
              </g>);
            }
            const city=cities.find(c=>c.display===hov);
            if (!city) return null;
            const [cx,cy]=projectIndia(city.coords[0],city.coords[1],VW,VH);
            const tx=cx>VW*0.7?cx-134:cx+14; const ty=cy>VH*0.8?cy-72:cy-8;
            return (<g>
              <rect x={tx} y={ty} width={124} height={66} rx={6} fill="#0E0C22" stroke="#2A2050" strokeWidth={1}/>
              <text x={tx+10} y={ty+18} fontFamily="'Playfair Display',serif" fontSize={13} fill="#EDE0CC" fontStyle="italic">{city.display}</text>
              {city.sellers>0&&<text x={tx+10} y={ty+35} fontFamily="'DM Mono',monospace" fontSize={9} fill="#D97706">✦ {city.sellers} seller{city.sellers>1?"s":""}</text>}
              {city.buyers>0&&<text x={tx+10} y={ty+(city.sellers>0?49:35)} fontFamily="'DM Mono',monospace" fontSize={9} fill="#059669">● {city.buyers} buyer{city.buyers>1?"s":""}</text>}
            </g>);
          })()}
        </svg>
      </div>

      <div style={{display:"flex",gap:18,marginTop:12,flexWrap:"wrap",alignItems:"center"}}>
        {[["#D97706","●","SELLER"],["#059669","●","BUYER"],["#059669","◆","UNIVERSE VISITOR"]].map(([col,icon,label])=>(
          <div key={label} style={{display:"flex",alignItems:"center",gap:5}}>
            <span style={{color:col,fontSize:label.includes("VISITOR")?11:9,lineHeight:1}}>{icon}</span>
            <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#3A3050",letterSpacing:"0.08em"}}>{label}</span>
          </div>
        ))}
      </div>

      {recentVisitors.length>0&&(
        <div style={{marginTop:24}}>
          <div style={{display:"flex",alignItems:"baseline",gap:10,marginBottom:12}}>
            <h3 style={{fontFamily:"'Playfair Display',serif",fontSize:20,margin:0,color:"#EDE0CC",fontStyle:"italic"}}>Universe Visitors</h3>
            <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#3A3050",letterSpacing:"0.1em"}}>{recentVisitors.length} UNIQUE</span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:8}}>
            {recentVisitors.map(v=>(
              <div key={v.userId} style={{background:"#0A0818",border:"1px solid #1A1530",borderRadius:10,padding:"10px 12px",display:"flex",gap:8,alignItems:"center"}}>
                <div style={{width:32,height:32,borderRadius:"50%",background:`${roleColor(v.role)}18`,border:`1.5px solid ${roleColor(v.role)}40`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontFamily:"'DM Mono',monospace",fontSize:11,fontWeight:700,color:roleColor(v.role)}}>
                  {(v.userName||"?").charAt(0).toUpperCase()}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:500,color:"#C8C0B0",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v.userName||"Unknown"}</div>
                  <div style={{display:"flex",gap:5,alignItems:"center",marginTop:2}}>
                    <span style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:roleColor(v.role),letterSpacing:"0.08em",textTransform:"uppercase"}}>{v.role||"user"}</span>
                    {v.city&&<span style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"#3A3050"}}>· {v.city}</span>}
                  </div>
                </div>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"#2A2440",flexShrink:0}}>{timeAgoShort(v.visitedAt)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function UniverseView({ catalogEntries, watchModels, users=[], sellers=[], setView, session=null, isAdmin=false }) {
  const [mode, setMode]     = useState("catalog");
  const [visits, setVisits] = useState(null);

  // Apply aventurine dark theme to entire page
  useEffect(()=>{
    document.body.classList.add("universe-mode");
    return ()=>document.body.classList.remove("universe-mode");
  },[]);

  // Log visit (fire-and-forget, one record per user per hour)
  useEffect(()=>{
    if (!session?.user?.id) return;
    const u = session.user;
    const bucket = Math.floor(Date.now()/3600000);
    supabase.from("universe_visits").upsert({
      id:`${u.id}-${bucket}`, userId:u.id,
      userName:u.name||"Unknown", role:u.role||"buyer",
      city:u.addressCity||null, visitedAt:new Date().toISOString(),
    });
  },[session?.user?.id]);

  // Admin: fetch visits
  useEffect(()=>{
    if (!isAdmin) return;
    supabase.from("universe_visits").select("*").order("visitedAt",{ascending:false}).limit(200)
      .then(({data})=>setVisits(data||[]));
  },[isAdmin]);

  return (
    <>
    {/* Full-page artifact overlay — watches + gears fly across the whole viewport */}
    <FullPageArtifacts/>
    <div className="fade-in" style={{background:"#00000E",margin:"0 -24px",padding:"28px 24px",minHeight:"calc(100vh - 60px)",color:"#C8D4FF",position:"relative",zIndex:1}}>

      {/* Top bar */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24,flexWrap:"wrap",gap:12}}>
        <div>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:34,margin:0,letterSpacing:"-0.025em",color:"#EDE0CC",fontStyle:"italic"}}>
            Watch Universe
          </h1>
          <p style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#2A2440",letterSpacing:"0.1em",marginTop:4,marginBottom:0}}>
            EXPLORE THE HOROLOGICAL COSMOS
          </p>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
          {isAdmin&&(
            <div style={{display:"flex",gap:1,background:"#0A0818",border:"1px solid #1A1530",borderRadius:20,padding:3}}>
              {[["catalog","◉ Universe"],["community","◎ Community"]].map(([m,label])=>(
                <button key={m} onClick={()=>setMode(m)}
                  style={{padding:"6px 14px",borderRadius:16,border:"none",fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.07em",cursor:"pointer",transition:"all .2s",
                    background:mode===m?"#1A1530":"none",
                    color:mode===m?"#C8C0B0":"#2A2440"}}>
                  {label}
                </button>
              ))}
            </div>
          )}
          <button onClick={()=>setView("catalog")}
            style={{background:"none",border:"1px solid #1A1530",borderRadius:20,padding:"7px 16px",color:"#2A2440",fontFamily:"'DM Mono',monospace",fontSize:9,cursor:"pointer",letterSpacing:"0.07em",transition:"border-color .15s,color .15s"}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor="#3A3050";e.currentTarget.style.color="#6B60A0";}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor="#1A1530";e.currentTarget.style.color="#2A2440";}}>
            ← Catalog
          </button>
        </div>
      </div>

      {mode==="catalog"
        ? <SolarSystem catalogEntries={catalogEntries} watchModels={watchModels} setView={setView}/>
        : <CommunityMap users={users} sellers={sellers} visits={visits}/>
      }
    </div>
    </>
  );
}
